import PropTypes from 'prop-types';

// from https://github.com/facebook/react/issues/1715#issuecomment-168943070
const chainablePropType = (predicate) => {
  const propType = (props, propName, componentName) => {
    // don't do any validation if empty
    if (props[propName] === null || props[propName] === undefined) {
      return null;
    }

    return predicate(props, propName, componentName);
  };

  propType.isRequired = (props, propName, componentName) => {
    // warn if empty
    if (props[propName] === null || props[propName] === undefined) {
      return new Error(`Required prop \`${propName}\` was not specified in \`${componentName}\`.`);
    }

    return predicate(props, propName, componentName);
  };

  return propType;
};

const shapeWithTypeValues = (type, filterValue, props, propName, componentName) => {
  const prop = props[propName];
  if (typeof (prop) !== 'object') {
    return new Error(
      `Invalid prop \`${propName}\` supplied to \`${componentName}\`. Validation failed.`,
    );
  }

  const propKeys = Object.keys(prop);
  const invalidKeyValuePairs = propKeys.filter(propKey => (
    filterValue(prop[propKey], propKey)
  ));
  if (invalidKeyValuePairs.length) {
    return new Error(
      `Invalid prop \`${propName}\` supplied to \`${componentName}\`. \`${propName}[${invalidKeyValuePairs[0]}]\` is not a ${type}. Validation failed.`,
    );
  }

  return null;
};

const shapeWithBooleanValues = (props, propName, componentName) => {
  const filterBoolean = value => (
    typeof (value) !== 'boolean'
  );
  return shapeWithTypeValues('boolean', filterBoolean, props, propName, componentName);
};

const shapeWithStringValues = (props, propName, componentName) => {
  const filterString = value => (
    typeof (value) !== 'string'
  );
  return shapeWithTypeValues('string', filterString, props, propName, componentName);
};

const hashmapOf = (shapeValidator) => {
  if (!shapeValidator) {
    return new Error('hashmapOf Proptype must be called with a shape validator.');
  }

  const hashmapValidator = (props, propName, componentName) => {
    const filterHashmap = (hashmapValue, hashmapKey) => {
      const propTypes = {
        [`${propName}.${hashmapKey}`]: shapeValidator,
      };
      const propsToTest = {
        [`${propName}.${hashmapKey}`]: hashmapValue,
      };

      return PropTypes.checkPropTypes(propTypes, propsToTest, 'hashmap value', componentName);
    };
    return shapeWithTypeValues('hashmap', filterHashmap, props, propName, componentName);
  };

  return chainablePropType(hashmapValidator);
};

const BasePopupShape = {
  disabled: PropTypes.bool.isRequired,
  draggable: PropTypes.bool.isRequired,
  height: PropTypes.number,
  id: PropTypes.string.isRequired,
  pageIdx: PropTypes.number.isRequired,
  type: PropTypes.oneOf([
    'eraser',
    'highlighter',
    'image',
    'redact',
    'request-signature',
    'text',
  ]).isRequired,
  width: PropTypes.number,
  x: PropTypes.number.isRequired,
  y: PropTypes.number.isRequired,
  zIndex: PropTypes.number,
};

const ESignPopupShape = {
  eSignSettings: PropTypes.shape({
    address: PropTypes.string.isRequired,
  }),
};

const ImagePropertiesShape = {
  aspectRatio: PropTypes.number,
  imgIdx: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]).isRequired,
  imageUrl: PropTypes.string.isRequired,
  imgType: PropTypes.string,
};

const ImagePopupShape = {
  ...BasePopupShape,
  ...ImagePropertiesShape,
  type: PropTypes.oneOf(['image']).isRequired,
};
const ImagePopup = PropTypes.shape(ImagePopupShape);

const SignaturePopupShape = {
  ...ImagePopupShape,
  ...ESignPopupShape,
  imgType: PropTypes.oneOf(['signature']).isRequired,
};
const SignaturePopup = PropTypes.shape(SignaturePopupShape);

const BaseOverlayPopupShape = {
  ...ImagePopupShape,
  hovering: PropTypes.bool.isRequired,
  resizable: PropTypes.bool.isRequired,
  type: PropTypes.oneOf(['eraser', 'highlighter', 'redact']).isRequired,
};

const EraserPopupShape = {
  ...BaseOverlayPopupShape,
  type: PropTypes.oneOf(['eraser']).isRequired,
};
const EraserPopup = PropTypes.shape(EraserPopupShape);

const HighlighterPopupShape = {
  ...BaseOverlayPopupShape,
  type: PropTypes.oneOf(['highlighter']).isRequired,
};
const HighlighterPopup = PropTypes.shape(HighlighterPopupShape);

const RedactPopupShape = {
  ...BaseOverlayPopupShape,
  type: PropTypes.oneOf(['redact']).isRequired,
};
const RedactPopup = PropTypes.shape(RedactPopupShape);

const OverlayPopup = PropTypes.oneOfType([
  EraserPopup,
  HighlighterPopup,
  RedactPopup,
]);

const TextPropertiesShape = {
  ...ESignPopupShape,
  backgroundColor: PropTypes.string,
  bold: PropTypes.bool.isRequired,
  color: PropTypes.string,
  fontFamily: PropTypes.string.isRequired,
  fontSize: PropTypes.number.isRequired,
  italic: PropTypes.bool.isRequired,
  placeholderText: PropTypes.string,
  text: PropTypes.string.isRequired,
};

const TextPopupShape = {
  ...BasePopupShape,
  ...TextPropertiesShape,
  type: PropTypes.oneOf(['text']).isRequired,
};
const TextPopup = PropTypes.shape(TextPopupShape);

const BaseRequestSignaturePopupShape = {
  ...BasePopupShape,
  requestSettings: PropTypes.shape({
    address: PropTypes.string,
    aspectRatio: PropTypes.number,
    height: PropTypes.number,
    title: PropTypes.string.isRequired,
    width: PropTypes.number,
  }).isRequired,
  requestType: PropTypes.oneOf(['date', 'sign', 'text']).isRequired,
  resizable: PropTypes.bool.isRequired,
  type: PropTypes.oneOf(['request-signature']).isRequired,
};

const RequestSignatureSignPopupShape = {
  ...BaseRequestSignaturePopupShape,
  contentSettings: PropTypes.shape({
    ...ImagePropertiesShape,
    type: PropTypes.string.isRequired,
  }),
  requestSettings: PropTypes.shape({
    address: PropTypes.string,
    aspectRatio: PropTypes.number,
    height: PropTypes.number,
    title: PropTypes.string.isRequired,
    width: PropTypes.number,
  }).isRequired,
  requestType: PropTypes.oneOf(['sign']).isRequired,
};
const RequestSignatureSignPopup = PropTypes.shape(RequestSignatureSignPopupShape);

const RequestSignatureTextPopupShape = {
  ...BaseRequestSignaturePopupShape,
  contentSettings: PropTypes.shape({
    ...TextPropertiesShape,
    type: PropTypes.string.isRequired,
  }).isRequired,
  requestSettings: PropTypes.shape({
    address: PropTypes.string,
    title: PropTypes.string.isRequired,
  }).isRequired,
  requestType: PropTypes.oneOf(['text']).isRequired,
};
const RequestSignatureTextPopup = PropTypes.shape(RequestSignatureTextPopupShape);

const RequestSignatureDatePopupShape = {
  ...RequestSignatureTextPopupShape,
  requestType: PropTypes.oneOf(['date']).isRequired,
};
const RequestSignatureDatePopup = PropTypes.shape(RequestSignatureDatePopupShape);

// Covers any signature request field.
const RequestSignaturePopup = PropTypes.oneOfType([
  RequestSignatureDatePopup,
  RequestSignatureSignPopup,
  RequestSignatureTextPopup,
]);

// Covers either filled or unfilled signature popups.
const SendForSignaturePopup = PropTypes.oneOfType([
  RequestSignaturePopup,
  SignaturePopup,
  TextPopup,
]);

// Covers any known type of popup.
const Popup = PropTypes.oneOfType([
  ImagePopup,
  OverlayPopup,
  RequestSignaturePopup,
  SignaturePopup,
  TextPopup,
]);

const CustomPropTypes = {
  hashmapOf,
  EraserPopup,
  HighlighterPopup,
  ImagePopup,
  OverlayPopup,
  Popup,
  RedactPopup,
  RequestSignatureDatePopup,
  RequestSignaturePopup,
  RequestSignatureSignPopup,
  RequestSignatureTextPopup,
  SendForSignaturePopup,
  SignaturePopup,
  TextPopup,
  shapeWithBooleanValues: chainablePropType(shapeWithBooleanValues),
  shapeWithStringValues: chainablePropType(shapeWithStringValues),
};

export default CustomPropTypes;



// WEBPACK FOOTER //
// ./src-react/CustomPropTypes/index.jsx